// ─── Adding a booking, business-side ─────────────────────────────────────────
//
// The gap Drea found on 2026-09-12: there was no way to put a booking IN.
// Clicking empty space on the day grid blocked time out, and nothing anywhere
// created an appointment. Every booking in the system had to arrive through
// the customer page — useless for a phone call, a walk-in, or a regular who
// always books at the door.
//
// Three things to choose: who, what, when. In that order, because that is the
// order a phone call goes in — "Hello, it's Margaret" comes before "can I have
// a cut on Thursday".
//
// ── What this does NOT do ───────────────────────────────────────────────────
//
// It does not compute availability. Times come from the same engine the
// customer page uses, passed in by the caller. Two surfaces working out
// separately what is free is how they come to disagree, and the design doc
// rules it out directly.
//
// It does not decide that the booking is confirmed either — the API does, and
// the wording here has to match. A booking Carrie takes on the phone IS the
// approval, so the button says "Book", not "Request".

import { useMemo, useState } from "react";
import { matchClients, validateClient, type ClientRecord, type NewClient } from "./clients.js";

export type AddBookingService = {
  id: string;
  name: string;
  durationMinutes: number;
  priceMinor?: number | null;
  currency?: string | null;
};

export type AddBookingSlot = { startsAt: Date; endsAt: Date };

export type AddBookingProps = {
  /** The day being added to. Times offered are this day's. */
  date: Date;
  clients: ClientRecord[];
  services: AddBookingService[];
  /** Free times for the chosen service on this day, from the availability
   *  engine. The caller fetches them; this never derives them. */
  slotsFor: (service: AddBookingService) => AddBookingSlot[];
  /** Create a client the salon has not met before. Returns the saved record,
   *  so the picker can select the real person rather than a local guess. */
  onCreateClient: (draft: NewClient) => Promise<ClientRecord>;
  onSubmit: (input: {
    client: ClientRecord;
    service: AddBookingService;
    slot: AddBookingSlot;
    clientActionId: string;
  }) => Promise<unknown>;
  onCancel: () => void;
  /** Injectable so tests are deterministic; defaults to a real random id. */
  newActionId?: () => string;
  /** The product's words — content, not layout. A hall takes bookings from
   *  "hirers" for "hire types"; a salon from clients for services. */
  nouns?: { client?: string; clients?: string; services?: string; what?: string };
};

const money = (minor?: number | null, currency?: string | null) =>
  minor == null ? null : new Intl.NumberFormat("en-GB", { style: "currency", currency: currency ?? "GBP" }).format(minor / 100);

const timeOf = (d: Date) =>
  d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

export function AddBooking({
  date, clients, services, slotsFor, onCreateClient, onSubmit, onCancel,
  newActionId = () => (globalThis.crypto?.randomUUID?.() ?? `id-${Date.now()}-${Math.random()}`),
  nouns,
}: AddBookingProps) {
  const clientWord = nouns?.client ?? "client";
  const clientsWord = nouns?.clients ?? "clients";
  const servicesWord = nouns?.services ?? "services";
  const [query, setQuery] = useState("");
  const [client, setClient] = useState<ClientRecord | null>(null);
  const [service, setService] = useState<AddBookingService | null>(null);
  const [slot, setSlot] = useState<AddBookingSlot | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<NewClient>({ fullName: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ONE action id per attempt, minted when the form opens and reused by every
  // retry of the same booking. Minting it at submit time would make each retry
  // a new booking, which is the whole thing replay protection exists to stop.
  const [actionId, setActionId] = useState(newActionId);

  const matches = useMemo(() => matchClients(clients, query), [clients, query]);
  const slots = useMemo(() => (service ? slotsFor(service) : []), [service, slotsFor]);
  const problems = adding ? validateClient(draft) : [];

  const heading = date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  async function saveNewClient() {
    if (validateClient(draft).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await onCreateClient(draft);
      // The SAVED record, not the draft — the server may have matched an
      // existing client by phone, and booking the local guess would attach the
      // appointment to a person who does not exist.
      setClient(saved);
      setAdding(false);
      setDraft({ fullName: "", phone: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not add the ${clientWord}.`);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!client || !service || !slot) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ client, service, slot, clientActionId: actionId });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not save the booking.";
      setError(message);
      // A slot that has gone is the one failure where retrying the SAME action
      // id is wrong: the booking never happened, and the next attempt is a
      // genuinely different one at a different time.
      if (/already booked|no longer available/i.test(message)) {
        setSlot(null);
        setActionId(newActionId());
      }
    } finally {
      setBusy(false);
    }
  }

  const ready = client && service && slot && !busy;

  return (
    <>
      <style>{ADD_CSS}</style>
      <section className="nl-add" aria-label={`Add a booking on ${heading}`} data-testid="add-booking">
        <header className="nl-add__head">
          <div>
            <div className="nl-add__eyebrow">Add booking</div>
            <h2 className="nl-add__title">{heading}</h2>
          </div>
          <button type="button" onClick={onCancel} className="nl-add__close" aria-label="Cancel">✕</button>
        </header>

        {error && <p role="alert" className="nl-add__error" data-testid="add-booking-error">{error}</p>}

        {/* ── 1. Who ───────────────────────────────────────────────────── */}
        <div className="nl-add__step">
          <h3 className="nl-add__legend">1. Who is it for?</h3>

          {client ? (
            <div className="nl-add__chosen" data-testid="add-booking-client">
              <div>
                <strong>{client.fullName}</strong>
                {client.phone && <div className="nl-add__sub">{client.phone}</div>}
              </div>
              <button type="button" className="nl-add__link" onClick={() => { setClient(null); setQuery(""); }}>
                Change
              </button>
            </div>
          ) : adding ? (
            <div className="nl-add__form">
              <label className="nl-add__label">
                Name
                <input
                  className="nl-add__field"
                  value={draft.fullName}
                  autoComplete="name"
                  onChange={e => setDraft({ ...draft, fullName: e.target.value })}
                />
              </label>
              <label className="nl-add__label">
                Mobile
                <input
                  className="nl-add__field"
                  value={draft.phone}
                  inputMode="tel"
                  autoComplete="tel"
                  onChange={e => setDraft({ ...draft, phone: e.target.value })}
                />
              </label>
              {problems.map(p => (
                <p key={p.field} className="nl-add__hint" role="alert">{p.reason}</p>
              ))}
              <div className="nl-add__row">
                <button
                  type="button"
                  className="nl-add__primary"
                  disabled={problems.length > 0 || busy}
                  onClick={saveNewClient}
                  data-testid="add-booking-save-client"
                >{busy ? "Saving…" : `Save ${clientWord}`}</button>
                <button type="button" className="nl-add__secondary" onClick={() => setAdding(false)}>
                  Back to the list
                </button>
              </div>
            </div>
          ) : (
            <>
              <input
                className="nl-add__field"
                type="search"
                placeholder="Search by name or number"
                aria-label={`Search ${clientsWord}`}
                value={query}
                onChange={e => setQuery(e.target.value)}
                data-testid="add-booking-search"
              />
              <ul className="nl-add__list" data-testid="add-booking-clients">
                {matches.slice(0, 50).map(c => (
                  <li key={c.id}>
                    <button type="button" className="nl-add__option" onClick={() => setClient(c)}>
                      <span>
                        <strong>{c.fullName}</strong>
                        <span className="nl-add__sub">
                          {c.phone ?? "no number"}
                          {c.bookings > 0 && ` · ${c.bookings} booking${c.bookings === 1 ? "" : "s"}`}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
                {matches.length === 0 && (
                  <li className="nl-add__empty">
                    {clients.length === 0
                      ? `No ${clientsWord} yet.`
                      : "Nobody matches that."}
                  </li>
                )}
              </ul>
              <button
                type="button"
                className="nl-add__secondary"
                onClick={() => { setAdding(true); setDraft({ fullName: query, phone: "" }); }}
                data-testid="add-booking-new-client"
              >+ New {clientWord}</button>
            </>
          )}
        </div>

        {/* ── 2. What ──────────────────────────────────────────────────── */}
        {client && (
          <div className="nl-add__step">
            <h3 className="nl-add__legend">2. {nouns?.what ?? "What are they having?"}</h3>
            <div className="nl-add__chips">
              {services.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`nl-add__chip${service?.id === s.id ? " is-on" : ""}`}
                  onClick={() => { setService(s); setSlot(null); }}
                >
                  {s.name}
                  <span className="nl-add__sub">
                    {s.durationMinutes} min{money(s.priceMinor, s.currency) ? ` · ${money(s.priceMinor, s.currency)}` : ""}
                  </span>
                </button>
              ))}
              {services.length === 0 && <p className="nl-add__hint">No {servicesWord} set up yet.</p>}
            </div>
          </div>
        )}

        {/* ── 3. When ──────────────────────────────────────────────────── */}
        {client && service && (
          <div className="nl-add__step">
            <h3 className="nl-add__legend">3. What time?</h3>
            {slots.length === 0 ? (
              // Said plainly. An empty row of times with no explanation reads
              // as a broken screen rather than a full day.
              <p className="nl-add__hint" data-testid="add-booking-no-slots">
                Nothing free that day for {service.name}. Try another day, or block out
                the time and add it anyway from the day view.
              </p>
            ) : (
              <div className="nl-add__chips">
                {slots.map(s => (
                  <button
                    key={s.startsAt.toISOString()}
                    type="button"
                    className={`nl-add__chip nl-add__chip--time${slot?.startsAt.getTime() === s.startsAt.getTime() ? " is-on" : ""}`}
                    onClick={() => setSlot(s)}
                    data-testid="add-booking-slot"
                  >{timeOf(s.startsAt)}</button>
                ))}
              </div>
            )}
          </div>
        )}

        <footer className="nl-add__foot">
          <button
            type="button"
            className="nl-add__primary"
            disabled={!ready}
            onClick={submit}
            data-testid="add-booking-submit"
          >
            {busy ? "Saving…" : "Book it"}
          </button>
          <button type="button" className="nl-add__secondary" onClick={onCancel}>Cancel</button>
        </footer>

        {/* Says what will happen, in the words of what will actually happen.
            This is not a request awaiting anybody's approval. */}
        {ready && (
          <p className="nl-add__sub nl-add__confirm">
            {client!.fullName} · {service!.name} · {timeOf(slot!.startsAt)} — goes straight in the diary.
          </p>
        )}
      </section>
    </>
  );
}

const ADD_CSS = `
.nl-add {
  display: flex; flex-direction: column; gap: 16px;
  background: var(--nl-surface); color: var(--nl-ink);
  border: 1px solid var(--nl-stroke);
  border-radius: var(--nl-radius-card);
  padding: 16px;
}
.nl-add__head { display: flex; align-items: flex-start; gap: 12px; }
.nl-add__eyebrow {
  font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--nl-ink-faint);
}
.nl-add__title { margin: 2px 0 0; font-size: 1.05rem; font-weight: 600; }
.nl-add__close {
  margin-left: auto; min-height: var(--nl-hit-min); min-width: var(--nl-hit-min);
  border: 1px solid var(--nl-stroke); background: var(--nl-surface-raised);
  color: var(--nl-ink); border-radius: var(--nl-radius-control);
  font: inherit; cursor: pointer;
}
.nl-add__step { display: flex; flex-direction: column; gap: 8px; }
.nl-add__legend { margin: 0; font-size: 0.85rem; font-weight: 600; color: var(--nl-ink-dim); }
.nl-add__label { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; color: var(--nl-ink-dim); }
.nl-add__field {
  min-height: var(--nl-hit-min); border-radius: var(--nl-radius-control);
  border: 1px solid var(--nl-stroke); background: var(--nl-surface-raised);
  color: var(--nl-ink); padding: 0 12px; font: inherit; width: 100%;
}
.nl-add__form { display: flex; flex-direction: column; gap: 10px; }
.nl-add__row { display: flex; gap: 8px; flex-wrap: wrap; }
.nl-add__list {
  list-style: none; margin: 0; padding: 0;
  max-height: 220px; overflow-y: auto;
  display: flex; flex-direction: column; gap: 4px;
}
.nl-add__option {
  width: 100%; text-align: left; display: flex; gap: 8px;
  min-height: var(--nl-hit-min); padding: 8px 12px;
  border: 1px solid var(--nl-stroke-soft); background: var(--nl-surface-raised);
  color: var(--nl-ink); border-radius: var(--nl-radius-control);
  font: inherit; cursor: pointer;
}
.nl-add__option:hover { border-color: var(--nl-state-open); }
.nl-add__empty { color: var(--nl-ink-dim); padding: 8px 2px; font-size: 0.9rem; }
.nl-add__sub { display: block; font-size: 0.78rem; color: var(--nl-ink-dim); }
.nl-add__chosen {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 12px; border-radius: var(--nl-radius-control);
  background: var(--nl-surface-raised); border: 1px solid var(--nl-stroke);
}
.nl-add__link {
  margin-left: auto; background: none; border: none; color: var(--nl-ink-dim);
  text-decoration: underline; font: inherit; cursor: pointer;
  min-height: var(--nl-hit-min);
}
.nl-add__chips { display: flex; flex-wrap: wrap; gap: var(--nl-tile-gap); }
.nl-add__chip {
  min-height: var(--nl-hit-min); padding: 6px 14px; text-align: left;
  border: 1px solid var(--nl-stroke); background: var(--nl-surface-raised);
  color: var(--nl-ink);
  /* Absolute radius — a chip stretched by a long service name must still be a
     rectangle, not a capsule. */
  border-radius: var(--nl-radius-chip);
  font: inherit; cursor: pointer;
}
.nl-add__chip.is-on {
  background: var(--nl-state-selected); color: var(--nl-state-selected-ink);
  border-color: var(--nl-state-selected);
}
.nl-add__chip--time { font-variant-numeric: tabular-nums; }
.nl-add__foot { display: flex; gap: 8px; flex-wrap: wrap; }
.nl-add__primary {
  min-height: var(--nl-hit-min); padding: 0 18px; font-weight: 600;
  border-radius: var(--nl-radius-control); border: 1px solid var(--nl-stroke);
  background: var(--nl-state-open); color: var(--nl-state-open-ink);
  font-family: inherit; font-size: inherit; cursor: pointer;
}
.nl-add__primary:disabled { opacity: 0.5; cursor: not-allowed; }
.nl-add__secondary {
  min-height: var(--nl-hit-min); padding: 0 16px;
  border-radius: var(--nl-radius-control); border: 1px solid var(--nl-stroke);
  background: var(--nl-surface-raised); color: var(--nl-ink);
  font: inherit; cursor: pointer;
}
.nl-add__hint { margin: 0; font-size: 0.82rem; color: var(--nl-ink-dim); }
.nl-add__error { margin: 0; color: var(--nl-status-declined); font-size: 0.9rem; }
.nl-add__confirm { margin: 0; }
`;
